import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {SegmentedControl} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {assert} from 'Asserts';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {isEnumOptionsArray} from './editors/EnumEditor';
import {IconSelectorFieldSettings} from './StepConfigEditorTypes';

type IconSelectorValue = string | number | null;

export interface IconEnumSelectorProps {
  disabled?: boolean;
  fieldSettings: IconSelectorFieldSettings;
  configShape: GetPipelineConfigSpecType<'enum'>;
  selectedOption: IconSelectorValue;
  onChangeConfig: (option: IconSelectorValue) => void;
  isExpandable?: boolean;
  isExpanded?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function IconEnumSelector({
  disabled = false,
  fieldSettings,
  configShape,
  selectedOption,
  onChangeConfig,
  isExpandable,
  isExpanded,
  dataTestAttrs = {},
}: IconEnumSelectorProps): JSX.Element {
  assert(
    !(fieldSettings.svgImagePaths != null && fieldSettings.iconNames != null),
    'Cannot have both svgImagePaths and iconNames defined in fieldSettings.'
  );

  const hasFieldSettingsIcon = (value: string | number): boolean => Boolean(fieldSettings.iconNames?.[value]);
  const hasFieldSettingsSvg = (value: string | number): boolean =>
    Boolean(fieldSettings.svgImagePaths?.[value]);

  const options = isEnumOptionsArray(configShape.choices)
    ? configShape.choices
        // Only render choices that have an icon or SVG path -- if there's no visual representation, don't render it
        .filter(
          (choice) =>
            Boolean(choice.iconName) ||
            Boolean(choice.svgImagePath) ||
            hasFieldSettingsIcon(choice.value) ||
            hasFieldSettingsSvg(choice.value)
        )
        .map((choice) => {
          const icon = choice.iconName ?? fieldSettings.iconNames?.[choice.value];
          const svgImagePath = choice.svgImagePath ?? fieldSettings.svgImagePaths?.[choice.value];
          return {
            value: String(choice.value),
            label: (
              <Tooltip isDisabled={!choice.displayName} text={choice.displayName}>
                <div aria-label={choice.displayName} className={css(styles.segmentedControlItem)}>
                  {svgImagePath ? (
                    <img src={svgImagePath} alt={choice.displayName} className={css(styles.iconImage)} />
                  ) : (
                    icon && <BenchlingIcon icon={icon} />
                  )}
                </div>
              </Tooltip>
            ),
          };
        })
    : configShape.choices
        // Only render choices that have an icon or SVG path -- if there's no visual representation, don't render it
        .filter((choice) => hasFieldSettingsIcon(choice) || hasFieldSettingsSvg(choice))
        .map((choice) => {
          const icon = fieldSettings.iconNames?.[choice];
          const svgImagePath = fieldSettings.svgImagePaths?.[choice];
          return {
            value: String(choice),
            label: (
              <Tooltip
                isDisabled={!fieldSettings.displayNames?.[choice]}
                text={fieldSettings.displayNames?.[choice]}
              >
                <div
                  aria-label={fieldSettings.displayNames?.[choice]}
                  className={css(styles.segmentedControlItem)}
                >
                  {svgImagePath ? (
                    <img
                      src={svgImagePath}
                      alt={fieldSettings.displayNames?.[choice]}
                      className={css(styles.iconImage)}
                    />
                  ) : (
                    icon && <BenchlingIcon icon={icon} />
                  )}
                </div>
              </Tooltip>
            ),
          };
        });

  const handleChange = useCallback(
    (value: string): void => {
      let matches: Array<string | number>;
      if (isEnumOptionsArray(configShape.choices)) {
        const matchedChoices = configShape.choices.filter((choice) => String(choice.value) === value);
        matches = matchedChoices.map((choice) => choice.value);
      } else {
        matches = configShape.choices.filter((choice) => String(choice) === value);
      }
      assert(
        matches.length > 0,
        `No match for value "${value}" in choices: ${JSON.stringify(configShape.choices)}`
      );

      assert(
        matches.length === 1,
        `Ambiguous match for value "${value}" in choices: ${JSON.stringify(configShape.choices)}`
      );
      const matchedChoice = matches[0];

      onChangeConfig(matchedChoice);
    },
    [configShape.choices, onChangeConfig]
  );

  return (
    <div className={css(styles.blockDisplay)} {...getTestProps(dataTestAttrs)}>
      <SegmentedControl
        data={options}
        value={String(selectedOption)}
        onChange={handleChange}
        disabled={disabled}
        fullWidth={true}
        classNames={{
          label: css(
            styles.segmentedControlLabel,
            isExpandable && expandableStyles.rightRadius,
            isExpanded && expandableStyles.bottomLeftRadius
          ),
          control: css(
            isExpandable && expandableStyles.rightRadius,
            isExpanded && expandableStyles.bottomLeftRadius
          ),
          root: css(
            isExpandable && expandableStyles.rightRadius,
            isExpanded && expandableStyles.bottomLeftRadius
          ),
        }}
      />
    </div>
  );
}

const expandableStyles = StyleSheet.create({
  rightRadius: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
  },
  bottomLeftRadius: {
    borderBottomLeftRadius: 0,
  },
});

const styles = StyleSheet.create({
  segmentedControlLabel: {
    padding: 0,
  },
  segmentedControlItem: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  blockDisplay: {
    width: '100%',
  },
  expanded: {
    marginBottom: '-16px',
  },
  iconImage: {
    height: '100%',
    objectFit: 'contain',
  },
});
